import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
    Box,
    Flex,
    VStack,
    HStack,
    Heading,
    Text,
    useToast,
    useColorMode,
    Button,
    IconButton,
    Card,
    CardBody,
    Skeleton,
    Badge,
    Tooltip,
    Input,

    FormErrorMessage,
    useBreakpointValue,
    Grid,
    GridItem,
    InputGroup,
    InputLeftElement,
    List,
    ListItem,
    Icon,
    AlertDialog,
    AlertDialogOverlay,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogBody,
    AlertDialogFooter,
} from '@chakra-ui/react';
import { MdAdd, MdDelete, MdEdit, MdRefresh, MdSearch } from 'react-icons/md';
import axios from '../utils/axiosConfig';
import Navigation from '../components/Navigation';
import RightPanel from '../components/RightPanel';
import { Helmet } from "react-helmet";
import { AssistantContext } from "../context/AssistantContext";
import NewAssistantModal from '../components/NewAssistantModal';
import { useSearchParams } from 'react-router-dom';

const AssistantPage = () => {
    const { colorMode } = useColorMode();
    const [assistants, setAssistants] = useState([]);
    const [selectedAssistant, setSelectedAssistant] = useState(null);
    const [knowledgeBaseFiles, setKnowledgeBaseFiles] = useState([]);
    const [tools, setTools] = useState([]);
    const [mcps, setMcps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDeleting, setIsDeleting] = useState(false);
    const [assistantToDelete, setAssistantToDelete] = useState(null);
    const toast = useToast();
    const [isNewAssistantOpen, setIsNewAssistantOpen] = useState(false);
    const isMobile = useBreakpointValue({ base: true, md: false });
    const [searchQuery, setSearchQuery] = useState('');
    const [formState, setFormState] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const cancelRef = useRef();
    const itemRefs = useRef({});
    const [instruction, setInstruction] = useState('');
    const [searchParams] = useSearchParams();
    const assistantIdFromUrl = searchParams.get('assistantId');

    const showSuccessToast = (title) => {
        toast({
            title,
            status: 'success',
            duration: 3000,
            isClosable: true,
        });
    };

    const showErrorToast = (title) => {
        toast({
            title,
            status: 'error',
            duration: 5000,
            isClosable: true,
        });
    };

    const fetchAssistants = async () => {
        try {
            const response = await axios.get('/api/protected/assistants');
            setAssistants(response.data);
            // return response.data?.length > 0 ? response.data[0] : null;
            return response.data;
        } catch (error) {
            showErrorToast('Failed to fetch assistants');
            console.error('Error fetching assistants:', error);
        }
    };

    const fetchKnowledgeBaseFiles = async () => {
        try {
            const response = await axios.get(`/api/protected/knowledgebase`);
            setKnowledgeBaseFiles(response.data.data);
        } catch (error) {
            showErrorToast('Failed to fetch knowledge base files');
            console.error('Error fetching knowledge base files:', error);
        }
    };

    const fetchTools = async () => {
        try {
            const response = await axios.get(`/api/protected/tools`);
            setTools(response.data);
        } catch (error) {
            showErrorToast('Failed to fetch tools');
            console.error('Error fetching tools:', error);
        }
    };

    const fetchMCPs = async () => {
        try {
            const response = await axios.get(`/api/protected/tools/mcp`);
            setMcps(response.data);
        } catch (error) {
            showErrorToast('Failed to fetch mcps');
            console.error('Error fetching mcps:', error);
        }
    };

    // Initial load effect
    useEffect(() => {
        const preload = async () => {
            setLoading(true);
            try {
                await Promise.all([
                    fetchKnowledgeBaseFiles(),
                    fetchTools(),
                    fetchMCPs()
                ]);
                const assistantsData = await fetchAssistants();
                const linkedAssistant = assistantIdFromUrl
                    ? assistantsData.find((assistant) => assistant._id === assistantIdFromUrl)
                    : null;
                const firstAssistant = assistantsData.length > 0 ? assistantsData[0] : null;
                setSelectedAssistant(linkedAssistant || firstAssistant);
            } finally {
                setLoading(false);
            }
        };

        preload();
    }, [assistantIdFromUrl]); // Run when deep-link target changes

    useEffect(() => {
        if (!selectedAssistant?._id) return;

        const ref = itemRefs.current[selectedAssistant._id];
        if (ref && ref.scrollIntoView) {
            ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [selectedAssistant]);

    // Effect to update form state when selectedAssistant changes
    useEffect(() => {
        if (selectedAssistant) {
            // Determine provider based on cartesiaVoiceId
            const provider = selectedAssistant.voiceProvider;
            console.log('3. provider', provider);
            setFormState({
                ...selectedAssistant,
                tools: Array.isArray(selectedAssistant.tools) ? selectedAssistant.tools : [],
                mcps: Array.isArray(selectedAssistant.mcps) ? selectedAssistant.mcps : []
            });
        }
    }, [selectedAssistant]); // Run when selectedAssistant changes

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormState(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSliderChange = (value, name) => {
        setFormState(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setFormState(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handleVoiceChange = (e) => {
        const { value } = e.target;
        setFormState(prev => ({
            ...prev,
            voiceProvider: value,
            // cartesiaVoiceId: '' // Reset voice when provider changes
        }));
    };

    // New handler to update both voice settings atomically
    const handleVoiceSettingsChange = (provider, voiceId) => {
        console.log('provider', provider, 'voiceId', voiceId);
        setFormState(prev => ({
            ...prev,
            voiceProvider: provider,
            cartesiaVoiceId: voiceId || ''
        }));
    };


    const handleUrlChange = (e) => {
        const { name, value } = e.target;
        const urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;

        if (!urlPattern.test(value) && value !== '') {
            setFormErrors((prevErrors) => ({
                ...prevErrors,
                url: 'Please enter a valid URL',
            }));
        } else {
            setFormErrors((prevErrors) => ({
                ...prevErrors,
                url: '',
            }));
        }

        setFormState({
            ...formState,
            [name]: value,
        });
    };



    useEffect(() => {
        if (selectedAssistant) {
            setFormState(selectedAssistant);
        }
    }, [selectedAssistant]);

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setFormErrors({}); // Clear previous errors

        try {
            // Transform knowledgebases to the format expected by the server
            const knowledgebases = Array.isArray(formState.knowledgebases)
                ? formState.knowledgebases.map(kb => ({ _id: kb._id }))
                : [];

            const payload = {
                name: formState.name,
                firstMessage: formState.firstMessage,
                systemPrompt: formState.systemPrompt,
                temperature: formState.temperature,
                maxTokens: formState.maxTokens,
                idleMessage: formState.idleMessage,
                transcriptionModelId: formState.transcriptionModelId,
                inputAudioNoiseReduction: formState.inputAudioNoiseReduction || 'none',
                voiceProvider: formState.voiceProvider,
                cartesiaVoiceId: formState.cartesiaVoiceId,
                callTimeout: formState.callTimeout,
                twilioRecording: formState.twilioRecording,
                attachConversationHistory: formState.attachConversationHistory,
                ignoreAudioDuringGreeting: formState.ignoreAudioDuringGreeting ?? false,
                allowInterruptionDuringGreeting: formState.allowInterruptionDuringGreeting ?? true,
                model: formState.model,
                knowledgebases: knowledgebases,
                tools: formState.tools || [],
                mcps: formState.mcps || [],
                hangupTrigger: formState.hangupTrigger || '',
                enableAiHangup: formState.enableAiHangup ?? true,
                wordsInterruption: formState.wordsInterruption ?? 3,
                hangupTimeout: formState.hangupTimeout || 0,
                webhooks: formState.webhooks || [],
                voicemailAction: formState.voicemailAction || 'skip',
                vadThreshold: formState.vadThreshold ?? 0.5,
                vadSilenceDurationMs: formState.vadSilenceDurationMs ?? 500,
                vadPrefixPaddingMs: formState.vadPrefixPaddingMs ?? 300,
                vadType: formState.vadType || 'server_vad',
                vadEagerness: formState.vadEagerness || 'medium',
                version: selectedAssistant?.__v,
                realtimeSpeed: formState.realtimeSpeed ?? 1.0,
            };

            console.log('Sending payload:', payload);
            const response = await axios.put(`/api/protected/assistants/${selectedAssistant._id}`, payload);
            console.log('Server response:', response.data);

            if (response.data.success) {
                setAssistants(prev =>
                    prev.map(assistant =>
                        assistant._id === selectedAssistant._id ? response.data.data : assistant
                    )
                );
                // Update selectedAssistant and formState with the response data
                const updatedAssistant = response.data.data;
                console.log('Updated assistant data:', updatedAssistant);

                // Preserve the current knowledge base selections and provider in the form state
                const updatedFormState = {
                    ...updatedAssistant,
                    knowledgebases: formState.knowledgebases // Keep the current knowledge base selections
                };

                setSelectedAssistant(updatedAssistant);
                setFormState(updatedFormState);
                console.log('Form state after update:', updatedFormState);

                toast({
                    title: 'Success',
                    description: 'Assistant updated successfully',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error updating assistant:', error);
            // Check if it's a validation error
            if (error.response?.data?.errors) {
                // Handle validation errors
                const validationErrors = error.response.data.errors;
                // Set form errors
                const newFormErrors = {};
                validationErrors.forEach(err => {
                    if (err.path === 'voiceProvider') {
                        newFormErrors.voiceProvider = err.msg;
                    }
                    toast({
                        title: 'Validation Error',
                        description: err.msg,
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                });
                setFormErrors(newFormErrors);
            } else {
                if (error.response?.status === 409) {
                    toast({
                        title: 'Conflict detected',
                        description: error.response?.data?.message || 'Another user updated this assistant. Reloaded latest version.',
                        status: 'warning',
                        duration: 5000,
                        isClosable: true,
                    });
                    await fetchAssistants();
                    return;
                }
                // Handle other errors
                toast({
                    title: 'Error',
                    description: error.response?.data?.message || 'Failed to update assistant',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (assistantToDelete) => {
        setIsDeleting(true);
        try {
            if (!assistantToDelete?.newAssistant) {
                await axios.delete(`/api/protected/assistants/${assistantToDelete._id}`);
            }
            setAssistants(prev => prev.filter(a => a._id !== assistantToDelete._id));
            setSelectedAssistant(assistants.length > 1 ? assistants[0] : null);
            toast({
                title: 'Success',
                description: 'Assistant deleted successfully',
                status: 'success',
                duration: 3000,
                isClosable: true
            });
        } catch (error) {
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to delete assistant',
                status: 'error',
                duration: 3000,
                isClosable: true
            });
            console.error('Error deleting assistant:', error);
        } finally {
            setIsDeleting(false);
            setIsDeleteDialogOpen(false);
            setAssistantToDelete(null);
        }
    };

    const handleDeleteClick = (e, assistant) => {
        e.stopPropagation();
        setAssistantToDelete(assistant);
        setIsDeleteDialogOpen(true);
    };

    const handleNewAssistant = () => {
        setIsNewAssistantOpen(true);
    };

    const handleAssistantCreated = (newAssistant) => {
        setAssistants(prev => [...prev, newAssistant]);
        setSelectedAssistant(newAssistant);
        setFormState(newAssistant);
        setTimeout(() => {
            const ref = itemRefs.current[newAssistant._id];
            if (ref && ref.scrollIntoView) {
                ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100);
    };

    const filteredAssistants = useMemo(() => {
        if (!searchQuery) return assistants;
        return assistants.filter(assistant =>
            assistant.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [assistants, searchQuery]);

    const onDuplicate = async () => {
        if (!selectedAssistant) return;
        try {
            const response = await axios.post(`/api/protected/assistants/${selectedAssistant._id}/duplicate`);
            const newAssistant = response.data.assistant;
            console.log('Duplicated Assistant:', newAssistant);
            const assistantData = await fetchAssistants();
            // find new assistant in updated list (in case it's enriched or sorted)
            const matched = assistantData.find((a) => {
                console.log('Comparing:', a._id, 'with', newAssistant._id);
                return a._id === newAssistant._id
            });

            setSelectedAssistant(matched);
            setFormState(matched);
            setTimeout(() => {
                const ref = itemRefs.current[newAssistant._id];
                if (ref && ref.scrollIntoView) {
                    ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100); // slight delay to ensure DOM is ready
            toast({
                title: 'Assistant Duplicated',
                description: 'The assistant has been duplicated successfully.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Error duplicating assistant:', error);
            toast({
                title: 'Error',
                description: 'Failed to duplicate the assistant. Please try again.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };


    return (
        <AssistantContext.Provider
            value={{
                assistants, setAssistants,
                selectedAssistant, setSelectedAssistant,
                knowledgeBaseFiles, tools, mcps,
                formState, setFormState,
                isSubmitting, setIsSubmitting,
                handleSubmit, handleDelete, onDuplicate,
                handleInputChange, handleSliderChange,
                handleCheckboxChange, handleVoiceChange,
                handleVoiceSettingsChange, handleUrlChange,
                showSuccessToast, showErrorToast, toast, formErrors,
                handleNewAssistant
            }}
        >
            <Helmet>
                <title>Assistants | AI Call System</title>
                <meta name="description" content="Create and manage your AI assistants for voice calls" />
            </Helmet>

            <Flex minH="100vh" bg={colorMode === 'light' ? 'gray.50' : 'gray.900'}>
                <Navigation />

                <Flex flex="1" direction={{ base: 'column', md: 'row' }} p={4} gap={4} h="100%">
                    {/* Assistants List */}
                    <Card
                        w={{ base: 'full', md: '300px' }}
                        bg={colorMode === 'light' ? 'white' : 'gray.800'}
                        borderRadius="lg"
                        boxShadow="sm"
                    >
                        <CardBody>
                            <Flex justify="space-between" align="center" mb={4}>
                                <Heading size="md" color={colorMode === 'light' ? 'gray.800' : 'white'}>Assistants</Heading>
                                <Tooltip label="Create new assistant">
                                    <IconButton
                                        icon={<MdAdd />}
                                        aria-label="New Assistant"
                                        colorScheme="brand"
                                        onClick={handleNewAssistant}
                                    />
                                </Tooltip>
                            </Flex>

                            <VStack spacing={2} align="stretch">
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none">
                                        <Icon as={MdSearch} color={colorMode === 'light' ? 'gray.400' : 'gray.500'} />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Search assistants..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        bg={colorMode === 'light' ? 'white' : 'gray.700'}
                                        borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                                        _hover={{
                                            borderColor: colorMode === 'light' ? 'gray.300' : 'gray.500'
                                        }}
                                    />
                                </InputGroup>

                                <Box
                                    maxH="calc(100vh - 200px)"
                                    overflowY="auto"
                                    p={2}
                                    borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                                    borderWidth={1}
                                    borderRadius="md"
                                    bg={colorMode === 'light' ? 'white' : 'gray.700'}
                                >
                                    {loading ? (
                                        Array(3).fill(0).map((_, i) => (
                                            <Skeleton key={i} height="60px" borderRadius="md" mb={2} />
                                        ))
                                    ) : filteredAssistants.length === 0 ? (
                                        <Text
                                            color={colorMode === 'light' ? 'gray.500' : 'gray.400'}
                                            textAlign="center"
                                            py={4}
                                        >
                                            {searchQuery ? 'No assistants match your search' : 'No assistants available'}
                                        </Text>
                                    ) : (
                                        <List spacing={3}>
                                            {filteredAssistants.map((assistant) => (
                                                <ListItem key={assistant._id} ref={(el) => (itemRefs.current[assistant._id] = el)}>
                                                    <Card
                                                        bg={selectedAssistant?._id === assistant._id ?
                                                            (colorMode === 'light' ? 'blue.500' : 'blue.800') :
                                                            (colorMode === 'light' ? 'white' : 'gray.800')
                                                        }
                                                        color={selectedAssistant?._id === assistant._id ?
                                                            'white' :
                                                            (colorMode === 'light' ? 'gray.800' : 'gray.100')
                                                        }
                                                        cursor="pointer"
                                                        onClick={() => setSelectedAssistant(assistant)}
                                                        _hover={{
                                                            bg: selectedAssistant?._id === assistant._id
                                                                ? (colorMode === 'light' ? 'blue.600' : 'blue.700')
                                                                : (colorMode === 'light' ? 'gray.50' : 'gray.600')
                                                        }}
                                                        transition="all 0.2s"
                                                        boxShadow="sm"
                                                        borderWidth="1px"
                                                        borderColor={selectedAssistant?._id === assistant._id ?
                                                            (colorMode === 'light' ? 'blue.600' : 'blue.700') :
                                                            (colorMode === 'light' ? 'gray.200' : 'gray.700')
                                                        }
                                                    >
                                                        <CardBody py={3}>
                                                            <Flex justify="space-between" align="center">
                                                                <VStack align="start" spacing={1}>
                                                                    <Text
                                                                        fontWeight="medium"
                                                                        color={selectedAssistant?._id === assistant._id ?
                                                                            'white' :
                                                                            (colorMode === 'light' ? 'gray.800' : 'blue.200')
                                                                        }
                                                                    >
                                                                        {assistant.name}
                                                                    </Text>
                                                                    <Text
                                                                        fontSize="xs"
                                                                        color={selectedAssistant?._id === assistant._id ?
                                                                            'whiteAlpha.800' :
                                                                            (colorMode === 'light' ? 'gray.500' : 'gray.400')
                                                                        }
                                                                    >
                                                                        {assistant._id || 'No description'}
                                                                    </Text>
                                                                </VStack>
                                                                <HStack>
                                                                    {assistant.isActive && (
                                                                        <Badge
                                                                            colorScheme={selectedAssistant?._id === assistant._id ?
                                                                                'blue' : 'green'
                                                                            }
                                                                            variant={selectedAssistant?._id === assistant._id ?
                                                                                'solid' : 'subtle'
                                                                            }
                                                                            fontSize="xs"
                                                                        >
                                                                            Active
                                                                        </Badge>
                                                                    )}
                                                                    <Tooltip label="Delete assistant">
                                                                        <IconButton
                                                                            icon={<MdDelete />}
                                                                            aria-label="Delete"
                                                                            size="sm"
                                                                            variant="ghost"
                                                                            color={selectedAssistant?._id === assistant._id ?
                                                                                'white' :
                                                                                (colorMode === 'light' ? 'gray.500' : 'gray.400')
                                                                            }
                                                                            _hover={{
                                                                                bg: selectedAssistant?._id === assistant._id
                                                                                    ? 'whiteAlpha.200'
                                                                                    : (colorMode === 'light' ? 'gray.100' : 'gray.600')
                                                                            }}
                                                                            onClick={(e) => handleDeleteClick(e, assistant)}
                                                                            isLoading={isDeleting}
                                                                        />
                                                                    </Tooltip>
                                                                </HStack>
                                                            </Flex>
                                                        </CardBody>
                                                    </Card>
                                                </ListItem>
                                            ))}
                                        </List>
                                    )}
                                </Box>

                                <Text
                                    fontSize="sm"
                                    color={colorMode === 'light' ? 'gray.500' : 'gray.400'}
                                    textAlign="right"
                                >
                                    {filteredAssistants.length} assistants
                                </Text>
                            </VStack>
                        </CardBody>
                    </Card>

                    {/* Assistant Details */}
                    <Box flex="1">
                        {selectedAssistant ? (
                            <>
                                <Text
                                    fontSize="sm"
                                    mb={3}
                                    color={colorMode === 'light' ? 'gray.600' : 'gray.300'}
                                >
                                    Last updated:{' '}
                                    {selectedAssistant.updatedAt
                                        ? new Date(selectedAssistant.updatedAt).toLocaleString()
                                        : 'Unknown time'}
                                    {' '}by{' '}
                                    {selectedAssistant.updatedBy
                                        ? [selectedAssistant.updatedBy.firstName, selectedAssistant.updatedBy.lastName]
                                            .filter(Boolean)
                                            .join(' ') || selectedAssistant.updatedBy.email || 'Unknown user'
                                        : 'Unknown user'}
                                </Text>
                                <RightPanel
                                // onNewAssistant={handleNewAssistant}
                                // selectedAssistant={selectedAssistant}
                                // onSave={handleSubmit}
                                // onDelete={handleDelete}
                                // knowledgeBaseFiles={knowledgeBaseFiles}
                                // tools={tools}
                                // formState={formState}
                                // handleInputChange={handleInputChange}
                                // handleSliderChange={handleSliderChange}
                                // handleCheckboxChange={handleCheckboxChange}
                                // handleVoiceChange={handleVoiceChange}
                                // handleVoiceSettingsChange={handleVoiceSettingsChange}
                                // handleUrlChange={handleUrlChange}
                                // isSubmitting={isSubmitting}
                                // formErrors={formErrors}
                                // setFormState={setFormState}
                                // onDuplicate={onDuplicate}
                                // mcps={mcps}
                                />
                            </>
                        ) : (
                            <Card
                                h="full"
                                bg={colorMode === 'light' ? 'white' : 'gray.800'}
                                borderRadius="lg"
                                boxShadow="sm"
                            >
                                <CardBody>
                                    <VStack spacing={4} align="center" justify="center" h="full">
                                        <Text fontSize="lg" color="gray.500">
                                            Select an assistant or create a new one to get started
                                        </Text>
                                        <Button
                                            leftIcon={<MdAdd />}
                                            colorScheme="brand"
                                            onClick={handleNewAssistant}
                                        >
                                            Create New Assistant
                                        </Button>
                                    </VStack>
                                </CardBody>
                            </Card>
                        )}
                    </Box>
                </Flex>
            </Flex>

            <NewAssistantModal
                isOpen={isNewAssistantOpen}
                onClose={() => setIsNewAssistantOpen(false)}
                onCreated={handleAssistantCreated}
            />

            {/* Delete Confirmation Dialog */}
            <AlertDialog
                isOpen={isDeleteDialogOpen}
                leastDestructiveRef={cancelRef}
                onClose={() => {
                    setIsDeleteDialogOpen(false);
                    setAssistantToDelete(null);
                }}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent bg={colorMode === 'light' ? 'white' : 'gray.800'}>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold" color={colorMode === 'light' ? 'gray.800' : 'white'}>
                            Delete Assistant
                        </AlertDialogHeader>
                        <AlertDialogBody color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>
                            Are you sure you want to delete this assistant? This action cannot be undone.
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button
                                ref={cancelRef}
                                onClick={() => {
                                    setIsDeleteDialogOpen(false);
                                    setAssistantToDelete(null);
                                }}
                                color={colorMode === 'light' ? 'gray.600' : 'gray.300'}
                            >
                                Cancel
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={() => handleDelete(assistantToDelete)}
                                ml={3}
                                isLoading={isDeleting}
                            >
                                Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </AssistantContext.Provider>
    );
};

export default AssistantPage;
